import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { colors, breakpoints } from '../../../assets/styles/GlobalStyles';
import { FaTimes, FaExchangeAlt, FaPaperPlane, FaCheckCircle, FaHourglassHalf } from 'react-icons/fa';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 1000;
  // A página de protocolos rola no próprio PageContainer: não deixa o gesto vazar pro fundo
  overscroll-behavior: contain;
  animation: ${fadeIn} 0.2s ease-out;
`;

const ModalBox = styled.div`
  background: ${colors.white};
  border-radius: 20px;
  width: 100%;
  max-width: 420px;
  padding: 1.75rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  animation: ${slideUp} 0.25s ease-out;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  max-height: calc(100vh - 3rem);
  max-height: calc(100dvh - 3rem);
  overflow-y: auto;
  overscroll-behavior: contain;

  @media (max-width: ${breakpoints.small}) {
    padding: 1.25rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.25rem;
  gap: 12px;
`;

const ModalTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a2c3e;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: ${colors.darkBlue};
  }
`;

const ModalSubtitle = styled.p`
  font-size: 0.8rem;
  color: #5a6e7c;
  margin: 0;
`;

const CloseButton = styled.button`
  background: ${colors.backgroundLight};
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #5a6e7c;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.borderLight};
    color: #1a2c3e;
  }
`;

const RouteRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: ${colors.backgroundLight};
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 1.5rem;
`;

const RouteDept = styled.div`
  flex: 1;
  min-width: 0;
  text-align: ${props => props.$align || 'left'};

  span {
    display: block;
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #94a3b8;
    margin-bottom: 4px;
  }

  strong {
    display: block;
    font-size: 0.88rem;
    color: #1a2c3e;
    overflow-wrap: break-word;
  }
`;

const RouteArrow = styled(FaExchangeAlt)`
  color: ${colors.lightBlue};
  flex-shrink: 0;
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
`;

const TimelineItem = styled.div`
  display: flex;
  gap: 12px;
  position: relative;
  padding-bottom: ${props => props.$last ? '0' : '24px'};

  &::before {
    content: '';
    position: absolute;
    left: 13px;
    top: 28px;
    bottom: 0;
    width: 2px;
    background: ${props => props.$done ? colors.lightBlue : colors.borderLight};
    display: ${props => props.$last ? 'none' : 'block'};
  }
`;

const TimelineIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${props => props.$done ? `${colors.darkBlue}15` : colors.backgroundLight};
  color: ${props => props.$done ? colors.darkBlue : '#94a3b8'};

  svg {
    font-size: 0.8rem;
  }
`;

const TimelineContent = styled.div`
  padding-top: 3px;
`;

const TimelineLabel = styled.div`
  font-size: 0.88rem;
  font-weight: 600;
  color: ${props => props.$done ? '#1a2c3e' : '#94a3b8'};
  margin-bottom: 2px;
`;

const TimelineDate = styled.div`
  font-size: 0.8rem;
  color: #5a6e7c;
`;

const TramitacaoModal = ({ protocolo, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    // Trava o scroll do fundo enquanto o modal está aberto (senão a lista
    // por trás rola junto com a rodinha do mouse). Quem rola é o #root,
    // não o body (que já é overflow hidden no index.html).
    const root = document.getElementById('root');
    const overflowOriginal = root ? root.style.overflow : '';
    if (root) root.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      if (root) root.style.overflow = overflowOriginal;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  if (!protocolo) return null;

  const foiEncaminhado = Boolean(protocolo.ultimoTramiteData);
  const foiRecebido = Boolean(protocolo.ultimoTramiteRecebimento);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <Overlay onClick={handleOverlayClick}>
      <ModalBox role="dialog" aria-modal="true" aria-labelledby="tramitacao-modal-titulo">
        <ModalHeader>
          <div>
            <ModalTitle id="tramitacao-modal-titulo">
              <FaExchangeAlt /> Andamento
            </ModalTitle>
            <ModalSubtitle>Protocolo nº {protocolo.numeroProtocolo}</ModalSubtitle>
          </div>
          <CloseButton onClick={onClose} aria-label="Fechar">
            <FaTimes />
          </CloseButton>
        </ModalHeader>

        <RouteRow>
          <RouteDept>
            <span>Atualmente</span>
            <strong>{protocolo.departamentoOrigem}</strong>
          </RouteDept>
          <RouteArrow />
          <RouteDept $align="right">
            <span>Destino</span>
            <strong>{protocolo.departamentoDestino}</strong>
          </RouteDept>
        </RouteRow>

        <Timeline>
          <TimelineItem $done={foiEncaminhado}>
            <TimelineIcon $done={foiEncaminhado}>
              <FaPaperPlane />
            </TimelineIcon>
            <TimelineContent>
              <TimelineLabel $done={foiEncaminhado}>Encaminhado</TimelineLabel>
              <TimelineDate>{foiEncaminhado ? protocolo.ultimoTramiteData : 'Ainda não encaminhado'}</TimelineDate>
            </TimelineContent>
          </TimelineItem>

          <TimelineItem $done={foiRecebido} $last>
            <TimelineIcon $done={foiRecebido}>
              {foiRecebido ? <FaCheckCircle /> : <FaHourglassHalf />}
            </TimelineIcon>
            <TimelineContent>
              <TimelineLabel $done={foiRecebido}>Recebido no destino</TimelineLabel>
              <TimelineDate>{foiRecebido ? protocolo.ultimoTramiteRecebimento : 'Aguardando recebimento'}</TimelineDate>
            </TimelineContent>
          </TimelineItem>
        </Timeline>
      </ModalBox>
    </Overlay>,
    document.body
  );
};

export default TramitacaoModal;
